import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { LayoutGrid, LogOut, Minimize2, Search } from 'lucide-react';
import type { User } from '@omnia/shared';
import { tr } from '../lib/i18n.ts';
import { useLogout } from '../lib/session.ts';
import { AppTile } from './AppTile.tsx';
import { ConfirmDiscard } from './ConfirmDiscard.tsx';
import { APPS } from './registry.ts';
import { filterApps } from './startMenu.ts';
import { useDesktop } from './store.ts';

const FOCUSABLE = 'button:not([disabled]), input:not([disabled])';

/** The Omnia button at the bottom-left and the menu that opens upward from it. */
export function StartMenu({ user }: { user: User }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const logout = useLogout();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuId = useId();
  const matches = filterApps(query, (id) => APPS[id].title);

  function close(restoreFocus = true) {
    setOpen(false);
    setQuery('');
    if (restoreFocus) buttonRef.current?.focus();
  }

  // Ctrl+Alt+O rather than the Windows/Meta key, which the browser and the OS keep for themselves.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!event.ctrlKey || !event.altKey || event.metaKey || event.shiftKey || event.code !== 'KeyO') return;
      event.preventDefault();
      setOpen((value) => !value);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) close(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  function launch(id: (typeof matches)[number]) {
    useDesktop.getState().open(id);
    close(false);
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }
    const items = [...(menuRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    const index = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'Tab' && items.length > 0) {
      // Focus stays inside the menu until it is closed.
      const next = (index + (event.shiftKey ? -1 : 1) + items.length) % items.length;
      event.preventDefault();
      items[next]?.focus();
    } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length > 0) {
      event.preventDefault();
      items[(index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
    }
  }

  const requestLogout = () => {
    close(false);
    if (useDesktop.getState().hasUnsaved()) setConfirmingLogout(true);
    else logout.mutate();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="start-button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={tr.startMenu.open}
        title={`${tr.startMenu.open} (${tr.startMenu.hint})`}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <img src="/brand/omnia-mark.png" alt="" width={28} height={28} draggable={false} />
      </button>
      {open && (
        <div ref={menuRef} id={menuId} className="start-menu" role="dialog" aria-label={tr.brandFull} onKeyDown={onKeyDown}>
          <header className="start-head">
            <img src="/brand/omnia-logo.png" alt="" width={104} height={37} draggable={false} />
            <h2>{tr.brandFull}</h2>
          </header>
          <label className="start-search">
            <Search size={14} aria-hidden />
            <input
              ref={searchRef}
              type="search"
              value={query}
              placeholder={tr.startMenu.search}
              aria-label={tr.startMenu.searchLabel}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && matches[0]) {
                  event.preventDefault();
                  launch(matches[0]);
                }
              }}
            />
          </label>
          <section className="start-section" aria-label={tr.startMenu.apps}>
            <h3>{tr.startMenu.apps}</h3>
            {matches.length === 0 ? (
              <p className="start-empty">{tr.startMenu.noMatch}</p>
            ) : (
              <ul className="start-apps">
                {matches.map((id) => (
                  <li key={id}>
                    <button type="button" className="start-app" onClick={() => launch(id)}>
                      <AppTile id={id} size={32} />
                      <span>{APPS[id].title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="start-section" aria-label={tr.startMenu.view}>
            <h3>{tr.startMenu.view}</h3>
            <div className="start-actions">
              <button
                type="button"
                className="start-action"
                onClick={() => {
                  useDesktop.getState().arrange();
                  close();
                }}
              >
                <LayoutGrid size={15} aria-hidden />
                {tr.menu.arrange}
              </button>
              <button
                type="button"
                className="start-action"
                onClick={() => {
                  useDesktop.getState().minimizeAll();
                  close();
                }}
              >
                <Minimize2 size={15} aria-hidden />
                {tr.menu.minimizeAll}
              </button>
            </div>
          </section>
          {!user.guest && (
            <footer className="start-foot">
              <span className="start-user">{tr.auth.welcome(user.name)}</span>
              <button type="button" className="start-action" onClick={requestLogout} disabled={logout.isPending}>
                <LogOut size={15} aria-hidden />
                {tr.auth.logout}
              </button>
            </footer>
          )}
        </div>
      )}
      {confirmingLogout && (
        <ConfirmDiscard
          variant="logout"
          onDiscard={() => {
            setConfirmingLogout(false);
            logout.mutate();
          }}
          onCancel={() => setConfirmingLogout(false)}
        />
      )}
    </>
  );
}
